/**
 * Lightweight, keyboard-accessible tooltip.
 *
 * Renders a trigger element with `aria-describedby` linking to a hidden tooltip
 * body that becomes visible on hover OR keyboard focus. UX-04 requires the
 * tooltip text to be reachable by Tab — Radix would do this for us, but we
 * keep the SPA dep footprint small in Phase 4.
 *
 * Behavior:
 * - The trigger is a `<button type="button">` so it joins the keyboard tab
 *   sequence naturally and screen readers announce the description.
 * - The tooltip body is rendered into the DOM at all times (so SR reads it via
 *   aria-describedby) but visually shown only when `data-visible="true"`.
 * - `Esc` while focused dismisses the tooltip (resets internal state).
 *
 * Carried forward verbatim from packages/spa/src/components/ui/Tooltip.tsx.
 */
import { useId, useState, type ReactNode, type KeyboardEvent } from 'react';

interface TooltipProps {
  /** What the user clicks/hovers/focuses (e.g., the "?" icon button). */
  trigger: ReactNode;
  /** What appears in the tooltip body (UX-04 → definitions.json formula text). */
  children: ReactNode;
  /** Optional override for the visually-rendered tooltip label. */
  label?: string;
}

export function Tooltip({ trigger, children, label }: TooltipProps): React.JSX.Element {
  const id = useId();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  const visible = hovered || focused;

  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>): void {
    if (e.key === 'Escape') {
      setHovered(false);
      setFocused(false);
      e.currentTarget.blur();
    }
  }

  return (
    <span className="relative inline-block">
      <button
        type="button"
        aria-label={label ?? 'Show definition'}
        aria-describedby={id}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-ink-200 text-xs font-semibold text-ink-500 hover:border-brand-500 hover:text-brand-600"
        onMouseEnter={() => {
          setHovered(true);
        }}
        onMouseLeave={() => {
          setHovered(false);
        }}
        onFocus={() => {
          setFocused(true);
        }}
        onBlur={() => {
          setFocused(false);
        }}
        onKeyDown={handleKeyDown}
      >
        {trigger}
      </button>
      <div
        id={id}
        role="tooltip"
        data-visible={visible}
        className={`absolute left-1/2 top-full z-20 mt-2 w-72 -translate-x-1/2 rounded-lg border border-ink-200 bg-white p-3 text-xs leading-relaxed text-ink-700 shadow-lg ${
          visible ? 'visible opacity-100' : 'invisible opacity-0'
        } transition-opacity duration-150`}
      >
        {children}
      </div>
    </span>
  );
}
